import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CivilAppellateSaveStep4 } from '../../types/civilAppellateApi';
import type { CriminalSaveStep4 } from '../../types/criminalApi';
import type { CivilAppellateWizardDraft } from '../../types/civilAppellateCaseRequest';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateFormat';
import { useCivilAppellateLookups } from '../wizard/civilAppellate/useCivilAppellateLookups';
import { INCORPORATED_BODY_TYPE_LABELS } from '../wizard/civilAppellate/civilAppellateConstants';
import { isOtherCorporateInstitute } from '../wizard/civilAppellate/civilAppellateHelpers';
import { apiOrLookupLabel, deliveryMethodDisplay, submitterFromApiStep } from './viewRequestHelpers';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../wizard/criminal/CriminalReviewUi';
import { ConnectedAgdFileRefReviewField } from '../../components/view-request/ConnectedAgdFileRefReviewField';

type Props = {
  caseRequestId: string;
  draft: CivilAppellateWizardDraft;
  submittedAt: string;
  step4?: CivilAppellateSaveStep4;
};

function yesNoLabel(v: string): string {
  if (v === 'yes') return 'Yes';
  if (v === 'no') return 'No';
  return '—';
}

export default function ViewRequestCivilAppellateCaseDetailsTab({ caseRequestId, draft, submittedAt, step4 }: Props) {
  const { t } = useTranslation();
  const noData = t('common.noDataFound');
  const lookups = useCivilAppellateLookups('review', draft.provinceId);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(
      draft as unknown as CriminalWizardDraft,
      lookups.deliveryMethods,
      step4 as CriminalSaveStep4 | undefined,
      noData,
    ),
    [draft, lookups.deliveryMethods, step4, noData],
  );

  const submitter = useMemo(() => submitterFromApiStep(step4), [step4]);

  const instituteLabel =
    draft.instituteType === 'corporation'
      ? isOtherCorporateInstitute(draft.corporateInstituteId, draft.corporateInstituteName)
        ? [draft.corporateInstituteOther, draft.corporateInstituteName].filter(Boolean).join(' · ') ||
          'Corporation'
        : draft.corporateInstituteName || 'Corporation'
      : draft.instituteType === 'government'
        ? 'Government Department'
        : '—';

  const judgmentFiles = draft.judgmentOrOrderDocuments
    .map((f) => f.originalFileName || f.fileName)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="vcr-case-details">
      <p className="vcr-submitted-at">
        Request Submitted at <strong>{formatDisplayDateTime(submittedAt) || '—'}</strong>
      </p>

      <ReviewSection step="Step 01" title="Case Overview">
        <ReviewSubBlock title="Case Categorization">
          <ReviewGrid cols={4}>
            <ReviewField label="File Category" value={draft.fileCategory} />
            <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            <ReviewField label="Institute Type" value={instituteLabel} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
        </ReviewSubBlock>
        <ReviewSubBlock title="Case Details">
          <ReviewField label="Description" value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label="Appeal Matter" value={yesNoLabel(draft.isAppealMatter)} />
            {draft.isAppealMatter === 'yes' ? (
              <>
                <ReviewField label="Original Court" value={draft.originalCourt} />
                <ReviewField label="Judgement/Order" value={judgmentFiles || '—'} span={2} />
              </>
            ) : null}
            <ReviewField
              label="Province"
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label="District"
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            <ReviewField label="Your Reference Number (External)" value={draft.yourReferenceNumber} />
            <ReviewField label="Other External Reference Number(s)" value={draft.otherExternalReferences} />
            <ReviewField label="Case Number" value={draft.caseNumber} />
            <ReviewField label="Are there any connected matters (Cases)?" value={yesNoLabel(draft.hasConnectedMatters)} />
            {draft.hasConnectedMatters === 'yes' ? (
              <>
                <ReviewField label="Connected Case Number" value={draft.connectedCaseNumber} />
                <ConnectedAgdFileRefReviewField
                  caseRequestId={caseRequestId}
                  label="Connected AGD File Reference Number"
                  fileRef={draft.connectedAgdFileRef}
                />
              </>
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step="Step 02" title="Party Details">
        <ReviewGrid cols={4}>
          <ReviewField label="Plaintiff / Applicant Name" value={draft.plaintiffName} />
          <ReviewField label="Defendant / Respondent Name" value={draft.defendantName} />
          <ReviewField
            label="Select one of the following"
            value={
              draft.isIncorporatedBody
                ? INCORPORATED_BODY_TYPE_LABELS.incorporated
                : INCORPORATED_BODY_TYPE_LABELS.unincorporated
            }
            span={2}
          />
          <ReviewField label="Other Involved Institutions" value={draft.otherInvolvedInstitutions} span={2} />
          <ReviewField label="Name of the Legal Officer/ Officer In-Charge" value={draft.legalOfficerName} />
          <ReviewField label="Contact Number" value={draft.legalOfficerContact} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 03" title="Timeline">
        <ReviewGrid cols={3}>
          <ReviewField
            label="Urgency Level"
            value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
          />
          <ReviewField label="Deadline" value={formatDisplayDate(draft.deadline)} />
          <ReviewField label="Urgency Justification" value={draft.urgencyJustification} />
        </ReviewGrid>
      </ReviewSection>

      <ReviewSection step="Step 04" title="Submission & Physical File Delivery">
        <ReviewSubBlock title="Request Submitter's Details">
          <ReviewGrid cols={4}>
            <ReviewField label="Submitted Agency Name" value={submitter.agency} />
            <ReviewField label="Submitted Division/ Unit" value={submitter.division} />
            <ReviewField label="Designation" value={submitter.designation} />
            <ReviewField label="Submitted by" value={submitter.name} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label="Email Address" value={submitter.email} />
            <ReviewField label="Contact Number" value={submitter.contact} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title="Physical File Delivery Method">
          <ReviewField label="Physical File Delivery Method" value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>
    </div>
  );
}
